/**
 * Builds filterTypes for the ConditionRow component,
 * used in pricing rule clause conditions.
 *
 * Evaluates against event properties: custom attributes + derived attributes.
 */
export function buildPricingConditionFilterTypes(
  eventAttributes,
  stageOptions,
  dayOfWeekOptions,
  {
    equalityOperators,
    comparisonOperators,
    containmentOperators,
    dateOperators,
  },
  agentOptions = [],
  eventLabelOptions = [],
  derivedAttrLabels = {}
) {
  const types = [];

  // Custom event attributes
  (eventAttributes || []).forEach(d => {
    let inputType = 'plainText';
    let options = [];
    let operators = containmentOperators;

    if (d.attribute_display_type === 'list') {
      inputType = 'searchSelect';
      options = (d.attribute_values || []).map(v => ({ id: v, name: v }));
      operators = equalityOperators;
    } else if (d.attribute_display_type === 'checkbox') {
      inputType = 'booleanSelect';
      operators = equalityOperators;
    } else if (
      d.attribute_display_type === 'number' ||
      d.attribute_display_type === 'currency' ||
      d.attribute_display_type === 'personnel'
    ) {
      inputType = 'number';
      operators = comparisonOperators;
    } else if (d.attribute_display_type === 'person') {
      inputType = 'searchSelect';
      options = agentOptions;
      operators = equalityOperators;
    } else if (
      d.attribute_display_type === 'multiline_text' ||
      d.attribute_display_type === 'file'
    ) {
      inputType = 'plainText';
      operators = containmentOperators;
    } else if (d.attribute_display_type === 'date') {
      inputType = 'date';
      operators = dateOperators;
    } else if (d.attribute_display_type === 'time') {
      inputType = 'time';
      operators = comparisonOperators;
    }

    types.push({
      attributeKey: d.attribute_key,
      value: d.attribute_key,
      label: d.attribute_display_name,
      inputType,
      options,
      filterOperators: operators,
    });
  });

  // Derived attributes
  types.push({
    attributeKey: 'system_stage',
    value: 'system_stage',
    label: derivedAttrLabels.systemStage || 'System stage',
    inputType: 'searchSelect',
    options: stageOptions || [],
    filterOperators: equalityOperators,
  });

  types.push({
    attributeKey: 'event_label_id',
    value: 'event_label_id',
    label: derivedAttrLabels.eventLabel || 'Event label',
    inputType: 'searchSelect',
    options: eventLabelOptions || [],
    filterOperators: equalityOperators,
  });

  types.push({
    attributeKey: 'day_of_week',
    value: 'day_of_week',
    label: derivedAttrLabels.dayOfWeek || 'Day of week',
    inputType: 'searchSelect',
    options: dayOfWeekOptions || [],
    filterOperators: equalityOperators,
  });

  types.push({
    attributeKey: 'start_date',
    value: 'start_date',
    label: derivedAttrLabels.startDate || 'Start date',
    inputType: 'date',
    options: [],
    filterOperators: dateOperators,
  });

  types.push({
    attributeKey: 'duration_hours',
    value: 'duration_hours',
    label: derivedAttrLabels.durationHours || 'Total event duration',
    inputType: 'number',
    options: [],
    filterOperators: comparisonOperators,
  });

  types.push({
    attributeKey: 'number_of_days',
    value: 'number_of_days',
    label: derivedAttrLabels.numberOfDays || 'Number of days',
    inputType: 'number',
    options: [],
    filterOperators: comparisonOperators,
  });

  types.push({
    attributeKey: 'pax',
    value: 'pax',
    label: derivedAttrLabels.pax || 'Guest amount',
    inputType: 'number',
    options: [],
    filterOperators: comparisonOperators,
  });

  return types;
}

export function extractConditionPrimitive(value) {
  if (value != null && typeof value === 'object' && 'id' in value) {
    return value.id;
  }
  return value;
}

export function serializeConditionValues(values) {
  const list = Array.isArray(values)
    ? values
    : values != null && values !== ''
      ? [values]
      : [];

  return list.map(extractConditionPrimitive).filter(v => v != null && v !== '');
}

export function normalizeConditionValueForEditor(values, filterType) {
  if (!filterType) {
    const raw = Array.isArray(values) ? values[0] : values;
    return raw ?? '';
  }

  if (['searchSelect', 'booleanSelect'].includes(filterType.inputType)) {
    const raw = Array.isArray(values) ? values[0] : values;
    if (raw == null || raw === '') return {};

    const id = extractConditionPrimitive(raw);
    const option = filterType.options?.find(o => String(o.id) === String(id));
    return option
      ? { id: option.id, name: option.name }
      : { id, name: String(id) };
  }

  if (filterType.inputType === 'multiSelect') {
    return (Array.isArray(values) ? values : [values])
      .map(extractConditionPrimitive)
      .filter(v => v != null && v !== '');
  }

  const raw = Array.isArray(values) ? values[0] : values;
  return raw ?? '';
}
